"use client";

import { Button } from "@/components/ui/button";
import {
  getDefaultErrorTitle,
  getUserErrorMessage,
  type ErrorContext,
} from "@/lib/api/errors";
import { cn } from "@/lib/utils";
import {
  AlertTriangle,
  Info,
  type LucideIcon,
} from "lucide-react";

type ErrorStateVariant = "error" | "info" | "warning";

interface ErrorStateProps {
  title?: string;
  description?: string;
  error?: unknown;
  context?: ErrorContext;
  icon?: LucideIcon;
  variant?: ErrorStateVariant;
  actionLabel?: string;
  onAction?: () => void;
  className?: string;
}

const VARIANT_STYLES: Record<
  ErrorStateVariant,
  {
    container: string;
    iconWrap: string;
    title: string;
    defaultIcon: LucideIcon;
    button: "destructive" | "default" | "outline";
  }
> = {
  error: {
    container: "border-destructive/20 bg-destructive/5",
    iconWrap: "bg-destructive/10 text-destructive",
    title: "text-destructive",
    defaultIcon: AlertTriangle,
    button: "outline",
  },
  warning: {
    container: "border-amber-500/25 bg-amber-500/5",
    iconWrap: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
    title: "text-foreground",
    defaultIcon: AlertTriangle,
    button: "outline",
  },
  info: {
    container: "border-border/80 bg-muted/20",
    iconWrap: "bg-primary/10 text-primary",
    title: "text-foreground",
    defaultIcon: Info,
    button: "default",
  },
};

export default function ErrorState({
  title,
  description,
  error,
  context = "generic",
  icon,
  variant = "error",
  actionLabel,
  onAction,
  className,
}: ErrorStateProps) {
  const styles = VARIANT_STYLES[variant];
  const Icon = icon ?? styles.defaultIcon;

  const displayTitle = title ?? getDefaultErrorTitle(context);

  const resolvedDescription =
    description ??
    (error != null
      ? getUserErrorMessage(error, context)
      : variant === "info"
        ? "There is nothing to show here yet."
        : "We couldn't load this page. Please try again, or contact support if the problem continues.");

  return (
    <div
      role={variant === "error" ? "alert" : "status"}
      className={cn(
        "flex min-h-64 flex-col items-center justify-center rounded-lg border px-6 py-10 text-center shadow-sm",
        styles.container,
        className,
      )}
    >
      <div
        className={cn(
          "mb-4 flex size-12 items-center justify-center rounded-md",
          styles.iconWrap,
        )}
      >
        <Icon className="size-6" aria-hidden />
      </div>

      <h3 className={cn("text-lg font-semibold tracking-tight", styles.title)}>
        {displayTitle}
      </h3>

      <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
        {resolvedDescription}
      </p>

      {actionLabel && onAction ? (
        <Button
          variant={styles.button}
          size="sm"
          className="mt-6"
          onClick={onAction}
        >
          {actionLabel}
        </Button>
      ) : null}
    </div>
  );
}
